import { useContext } from 'react';

import { AddRounded, CloseRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  Theme,
} from '@mui/material';

import { useLanguage } from '../../hooks';
import { useMetersQuery } from '../../queries/assets';
import { FormBuilderContext } from './FormBuilderProvider';

interface MeterSettingProps {
  questionIndex: number;
  questionId: string;
  meterId: string;
  tableId?: string;
  associatedQuestions: string[];
  onMeterChange: (questionId: string, value: string, tableId?: string) => void;
  onChangeMeterQuestion: (
    questionId: string,
    action: string,
    tableId?: string,
    index?: number,
    value?: string,
  ) => void;
  onCreateNumberQuestion: (index: number, tableId?: string) => void;
}

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  formControlText: {
    margin: '12px 0px 4px 0px',
  },
  select: {
    height: '34px',
  },
  menuItem: { fontSize: '12px' },
  addQuestionButton: {
    fontSize: '12px',
    lineHeight: '20px',
    alignSelf: 'flex-start',
    height: '24px',
    '& svg': {
      width: '14px',
      height: '14px',
    },
  },
  questionContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  meterSelector: {
    paddingRight: '42px',
  },
  formControlWrapper: {
    flex: 1,
  },
  closeButton: {
    padding: '4px',
    height: '34px',
    width: '34px',
  },
};

export function MeterSetting({
  questionIndex,
  questionId,
  meterId,
  tableId,
  associatedQuestions,
  onMeterChange,
  onChangeMeterQuestion,
  onCreateNumberQuestion,
}: MeterSettingProps) {
  const lang = useLanguage();
  const { questions } = useContext(FormBuilderContext);
  const { data: metersData } = useMetersQuery();

  const availableQuestions = tableId
    ? questions
        .find((q) => q.id === tableId)
        ?.questions.filter((question) => question.type === 'number')
    : questions.filter((question) => question.type === 'number');

  return (
    <Box sx={customStyles.root}>
      <FormControl sx={customStyles.meterSelector}>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.selectMeter}
        </FormHelperText>
        <Select
          disableUnderline
          value={meterId}
          sx={customStyles.select}
          onChange={(event) => onMeterChange(questionId, event.target.value, tableId)}
        >
          {metersData?.meters?.map((meter, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={meter.id}
            >
              {meter.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {associatedQuestions?.length > 0 &&
        associatedQuestions.map((question, index) => (
          <Box
            key={`associated-question-${index}`}
            sx={customStyles.questionContainer}
          >
            <FormControl sx={customStyles.formControlWrapper}>
              <FormHelperText sx={customStyles.formControlText}>
                {lang.form_builder.selectMeterQuestion}
              </FormHelperText>
              <Select
                disableUnderline
                value={question}
                sx={customStyles.select}
                onChange={(event) =>
                  onChangeMeterQuestion(questionId, 'update', tableId, index, event.target.value)
                }
              >
                {availableQuestions
                  .filter((availableQuestion) => {
                    const otherSelectedQuestions = associatedQuestions.filter(
                      (_, i) => i !== index,
                    );
                    return (
                      availableQuestion.id === question ||
                      !otherSelectedQuestions.includes(availableQuestion.id)
                    );
                  })
                  .map((availableQuestion) => (
                    <MenuItem
                      key={availableQuestion.id}
                      sx={customStyles.menuItem}
                      value={availableQuestion.id}
                    >
                      {availableQuestion.name}
                    </MenuItem>
                  ))}
                <MenuItem>
                  <Button
                    variant='text'
                    color='primary'
                    startIcon={<AddRounded />}
                    sx={{
                      ...customStyles.addQuestionButton,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                    onClick={() => onCreateNumberQuestion(questionIndex, tableId)}
                  >
                    {lang.form_builder.createNumberQuestion}
                  </Button>
                </MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={() => onChangeMeterQuestion(questionId, 'delete', tableId, index)}
              sx={customStyles.closeButton}
              size='small'
            >
              <CloseRounded fontSize='small' />
            </IconButton>
          </Box>
        ))}
      <Button
        variant='text'
        color='primary'
        startIcon={<AddRounded />}
        onClick={() => onChangeMeterQuestion(questionId, 'create', tableId)}
        sx={{ ...customStyles.addQuestionButton, marginTop: '12px' }}
      >
        {lang.form_builder.addMeterQuestion}
      </Button>
    </Box>
  );
}
